"use client";

import React, { useState, useEffect } from "react";
import { sellService, type SellPayload } from "../services/sellService";
import { userService } from "../services/services";
import { useTranslations } from "next-intl";
import { authStorage } from "../lib/authStorage";
import {
  getUserBankDetails,
  getUserGstNumber,
  getUserPincode,
} from "../lib/userDetails";
import type {
  ApiProduct,
  BuyDraftPayload,
  ExchangeDraftPayload,
  TradeModalProps,
  TradeVariant,
  UserDetails,
} from "../types/domain";
import { getApiErrorMessage } from "../config/toastHelpers";
import ModalLoadingOverlay from "./ModalLoadingOverlay";
import TradeModalFooter from "./TradeModalFooter";
import TradeModalBuyForm from "./TradeModalBuyForm";
import TradeModalExchangeForm from "./TradeModalExchangeForm";
import TradeModalSellForm from "./TradeModalSellForm";
import {
  CLS,
  initialBuy,
  initialEx,
  initialSell,
  inp,
  MODAL_CONFIG,
  parseMinOrderQuantity,
  parseNum,
  syncBuyField,
  syncExchangeField,
  syncSellField,
  validateBuy,
  validateExchange,
  validateSell,
} from "../constants";
import { Icon } from "./Icon";
import WhatsAppButton from "./WhatsappButton";
import axios from "axios";

export default function TradeModal({
  isOpen,
  onClose,
  onSuccess,
  variant,
  selectedProduct,
}: TradeModalProps) {
  const t = useTranslations("tradeModal");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [buy, setBuy] = useState(initialBuy);
  const [sell, setSell] = useState(initialSell);
  const [ex, setEx] = useState(initialEx);
  const [buyMinQuantity, setBuyMinQuantity] = useState(1);
  const [buyProductPrice, setBuyProductPrice] = useState(0);
  const [sellMinQuantity, setSellMinQuantity] = useState(1);
  const [giveMinQuantity, setGiveMinQuantity] = useState(1);
  const [receiveMinQuantity, setReceiveMinQuantity] = useState(1);
  const [giveProductPrice, setGiveProductPrice] = useState(0);
  const [receiveProductPrice, setReceiveProductPrice] = useState(0);
  const [extraPayableAmount, setExtraPayableAmount] = useState(0);

  const tm = useTranslations("modals");
  const [submitError, setSubmitError] = useState("");
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  useEffect(() => {
    if (isOpen) {
      const user = authStorage.getUserDetails<Record<string, string>>();
      const preselectedId = selectedProduct?.id ?? "";
      if (user) {
        setBuy((prev) => ({
          ...prev,
          fullName: user?.name || user?.fullName || prev.fullName,
          address: user?.address || prev.address,
          pincode: user?.pinCode || user?.pincode || prev.pincode,
          gst: getUserGstNumber(user) || prev.gst,
          ...(preselectedId ? { product: preselectedId } : {}),
        }));
        setSell((prev) => ({
          ...prev,
          fullName: user?.name || user?.fullName || prev.fullName,
          phone: user?.phoneNumber || user?.phone || user?.mobile || prev.phone,
          gst: getUserGstNumber(user) || prev.gst,
          paymentDetails: getUserBankDetails(user) || prev.paymentDetails,
          ...(preselectedId ? { product: preselectedId } : {}),
        }));
        setEx((prev) => ({
          ...prev,
          fullName: user?.name || user?.fullName || prev.fullName,
          gst: getUserGstNumber(user) || prev.gst,
          ...(preselectedId ? { giveProduct: preselectedId } : {}),
        }));
      } else if (preselectedId) {
        setBuy((prev) => ({ ...prev, product: preselectedId }));
        setSell((prev) => ({ ...prev, product: preselectedId }));
        setEx((prev) => ({ ...prev, giveProduct: preselectedId }));
      }
    }
  }, [isOpen, selectedProduct]);

  const [buyProducts, setBuyProducts] = useState<
    Array<{ id: string; name: string }>
  >([]);
  useEffect(() => {
    if (!isOpen) return;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const [sellProducts, setSellProducts] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [exchangeProducts, setExchangeProducts] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [allProducts, setAllProducts] = useState<
    Array<{ id: string; name: string; dontExchange?: boolean }>
  >([]);

  useEffect(() => {
    const loadProducts = async () => {
      userService
        .getProductsByType("buy")
        .then(setBuyProducts)
        .catch(() => setBuyProducts([]));
      userService
        .getProductsByType("sell")
        .then(setSellProducts)
        .catch(() => setSellProducts([]));
      userService
        .getProductsByType("exchange")
        .then(setExchangeProducts)
        .catch(() => setExchangeProducts([]));
      userService
        .getProducts()
        .then((res) => {
          if (Array.isArray(res)) {
            setAllProducts(
              res.map((p: ApiProduct) => ({
                id: String(p.id ?? p._id ?? p.value ?? p.name ?? ""),
                name: String(p.name ?? p.label ?? p.value ?? ""),
                dontExchange: p.dontExchange,
              })),
            );
          } else {
            setAllProducts([]);
          }
        })
        .catch(() => setAllProducts([]));
    };
    loadProducts();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadBuyMinQuantity = async () => {
      if (!isOpen || variant !== "buy" || !buy.product) {
        setBuyMinQuantity(1);
        setBuyProductPrice(0);
        return;
      }

      try {
        const product = (await userService.getProductById(buy.product)) as {
          minOrderQuantity?: unknown;
          buyPrice?: string;
          price?: string;
        } | null;
        if (cancelled) return;
        const minQty = parseMinOrderQuantity(
          (product as { minOrderQuantity?: unknown } | null)?.minOrderQuantity,
        );
        const price =
          parseFloat(product?.buyPrice ?? product?.price ?? "0") || 0;
        setBuyMinQuantity(minQty);
        setBuyProductPrice(price);
        setBuy((prev) => {
          const parsed = Number.parseInt(prev.quantity, 10);
          const isValidQuantity =
            Number.isFinite(parsed) &&
            parsed >= minQty &&
            parsed % minQty === 0;
          if (isValidQuantity) return prev;
          return { ...prev, quantity: String(minQty) };
        });
        setFieldErrors((prev) => ({
          ...prev,
          product: "",
          quantity: "",
        }));
      } catch {
        if (cancelled) return;
        setBuyMinQuantity(1);
        setBuyProductPrice(0);
      }
    };
    loadBuyMinQuantity();
    return () => {
      cancelled = true;
    };
  }, [buy.product, isOpen, variant]);

  useEffect(() => {
    let cancelled = false;
    const loadSellMinQuantity = async () => {
      if (!isOpen || variant !== "sell" || !sell.product) {
        setSellMinQuantity(1);
        return;
      }
      try {
        const product = (await userService.getProductById(sell.product)) as {
          minOrderQuantity?: unknown;
          sellPrice?: string;
          price?: string;
        } | null;
        if (cancelled) return;
        const minQty = parseMinOrderQuantity(product?.minOrderQuantity);
        setSellMinQuantity(minQty);
        const sellPrice = product?.sellPrice ?? product?.price ?? "";
        setSell((prev) => ({
          ...prev,
          quantity:
            prev.quantity && Number.parseInt(prev.quantity, 10) > 0
              ? prev.quantity
              : String(minQty),
          pricePerKg: sellPrice,
        }));
        setFieldErrors((prev) => ({
          ...prev,
          product: "",
          quantity: "",
          pricePerKg: "",
        }));
      } catch {
        if (cancelled) return;
        setSellMinQuantity(1);
      }
    };
    loadSellMinQuantity();
    return () => {
      cancelled = true;
    };
  }, [sell.product, isOpen, variant]);

  useEffect(() => {
    let cancelled = false;
    const loadGiveMinQuantity = async () => {
      if (!isOpen || variant !== "exchange" || !ex.giveProduct) {
        setGiveMinQuantity(1);
        setGiveProductPrice(0);
        return;
      }
      try {
        const product = (await userService.getProductById(ex.giveProduct)) as {
          minOrderQuantity?: unknown;
          sellPrice?: string;
          buyPrice?: string;
          price?: string;
        } | null;
        if (cancelled) return;
        const minQty = parseMinOrderQuantity(product?.minOrderQuantity);
        setGiveMinQuantity(minQty);
        const price =
          parseFloat(product?.sellPrice ?? product?.price ?? "0") || 0;
        setGiveProductPrice(price);
        setEx((prev) => {
          const parsed = Number.parseInt(prev.giveQuantity, 10);
          const isValidQuantity =
            Number.isFinite(parsed) &&
            parsed >= minQty &&
            parsed % minQty === 0;
          if (isValidQuantity) return prev;
          return { ...prev, giveQuantity: String(minQty) };
        });
        setFieldErrors((prev) => ({
          ...prev,
          giveProduct: "",
          giveQuantity: "",
        }));
      } catch {
        if (cancelled) return;
        setGiveMinQuantity(1);
        setGiveProductPrice(0);
      }
    };
    loadGiveMinQuantity();
    return () => {
      cancelled = true;
    };
  }, [ex.giveProduct, isOpen, variant]);

  useEffect(() => {
    let cancelled = false;
    const loadReceiveMinQuantity = async () => {
      if (!isOpen || variant !== "exchange" || !ex.receiveProduct) {
        setReceiveMinQuantity(1);
        setReceiveProductPrice(0);
        return;
      }
      try {
        const product = (await userService.getProductById(
          ex.receiveProduct,
        )) as {
          minOrderQuantity?: unknown;
          sellPrice?: string;
          buyPrice?: string;
          price?: string;
        } | null;
        if (cancelled) return;
        const minQty = parseMinOrderQuantity(product?.minOrderQuantity);
        setReceiveMinQuantity(minQty);
        const price =
          parseFloat(
            product?.buyPrice ?? product?.sellPrice ?? product?.price ?? "0",
          ) || 0;
        setReceiveProductPrice(price);
        setFieldErrors((prev) => ({
          ...prev,
          receiveProduct: "",
        }));
      } catch {
        if (cancelled) return;
        setReceiveMinQuantity(1);
        setReceiveProductPrice(0);
      }
    };
    loadReceiveMinQuantity();
    return () => {
      cancelled = true;
    };
  }, [ex.receiveProduct, isOpen, variant]);

  useEffect(() => {
    if (
      !ex.giveProduct ||
      !ex.receiveProduct ||
      giveProductPrice <= 0 ||
      receiveProductPrice <= 0
    ) {
      setExtraPayableAmount(0);
      setEx((prev) =>
        prev.receiveQuantity ? { ...prev, receiveQuantity: "" } : prev,
      );
      return;
    }
    const giveQty = parseNum(ex.giveQuantity);
    if (giveQty <= 0) {
      setExtraPayableAmount(0);
      setEx((prev) =>
        prev.receiveQuantity ? { ...prev, receiveQuantity: "" } : prev,
      );
      return;
    }
    const totalSendValue = giveQty * giveProductPrice;
    const exactReceiveQty = totalSendValue / receiveProductPrice;
    const receiveLotQty =
      Math.floor(exactReceiveQty / receiveMinQuantity) * receiveMinQuantity;
    const remainingValue = totalSendValue - receiveLotQty * receiveProductPrice;
    const extra = Math.round(remainingValue * 1.05 * 100) / 100;
    setEx((prev) => {
      const nextReceiveQuantity = String(receiveLotQty);
      if (prev.receiveQuantity === nextReceiveQuantity) return prev;
      return { ...prev, receiveQuantity: nextReceiveQuantity };
    });
    setExtraPayableAmount(extra > 0 ? extra : 0);
  }, [
    ex.giveProduct,
    ex.giveQuantity,
    ex.receiveProduct,
    giveProductPrice,
    receiveProductPrice,
    receiveMinQuantity,
  ]);

  const field =
    <T extends object>(
      setter: React.Dispatch<React.SetStateAction<T>>,
      syncField?: (key: keyof T, value: string) => void,
    ) =>
      (key: keyof T) =>
        (
          e: React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
          >,
        ) => {
          const value = e.target.value;

          setter((prev) => ({
            ...prev,
            [key]: value,
          }));

          setFieldErrors((prev) => ({
            ...prev,
            [key]: "",
          }));

          syncField?.(key, value);
        };

  const setBuyField = field(setBuy, syncBuyField);
  const setSellField = field(setSell, syncSellField);
  const setExField = field(setEx, syncExchangeField);

  useEffect(() => {
    if (!isOpen) {
      setError("");
      setLoading(false);
      setFieldErrors({});
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const cached = authStorage.getUserDetails<UserDetails>();
    if (cached) {
      setUserDetails(cached);
    } else {
      const userId = authStorage.getUserId();
      if (userId) {
        userService
          .getUserDetails(userId)
          .then((user) => {
            authStorage.setUserDetails(user);
            setUserDetails(user);
          })
          .catch(() => { });
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) return;
    setBuy(initialBuy);
    setSell(initialSell);
    setEx(initialEx);
    setBuyMinQuantity(1);
    setSellMinQuantity(1);
    setGiveMinQuantity(1);
    setReceiveMinQuantity(1);
    setGiveProductPrice(0);
    setReceiveProductPrice(0);
    setExtraPayableAmount(0);
    setSubmitError("");
    setUserDetails(null);
  }, [isOpen]);

  const buildDraft = (): {
    localBuyDraft: BuyDraftPayload | null;
    localSellPayload: SellPayload | null;
    localExchangeDraft: ExchangeDraftPayload | null;
  } | null => {
    setError("");
    setFieldErrors({});
    setSubmitError("");

    let errs: Record<string, string> = {};
    if (variant === "buy") errs = validateBuy(buy, t, buyMinQuantity);
    else if (variant === "sell") errs = validateSell(sell, t, sellMinQuantity);
    else errs = validateExchange(ex, t, giveMinQuantity);

    if (Object.keys(errs).length) {
      setFieldErrors(errs);
      return null;
    }

    let localBuyDraft: BuyDraftPayload | null = null;
    let localSellPayload: SellPayload | null = null;
    let localExchangeDraft: ExchangeDraftPayload | null = null;

    if (variant === "buy") {
      localBuyDraft = {
        productId: buy.product,
        quantity: Number(buy.quantity),
        operation: "BUY",
        fullName: buy.fullName,
        gstNumber: buy.gst || undefined,
        address: buy.address,
        pincode: buy.pincode,
      };
    } else if (variant === "sell") {
      localSellPayload = {
        productId: sell.product,
        quantity: Number(sell.quantity),
        amount: Number(sell.pricePerKg) * Number(sell.quantity) * 1.05,
        operation: "SELL",

        fullName: sell.fullName,
        phone: sell.phone,

        gstNumber: sell.gst,
        upiId: "",
        bankDetails: sell.paymentDetails,
        paymentQrCode: "",
      };
    } else {
      localExchangeDraft = {
        productToGiveId: ex.giveProduct,
        quantityToGive: Number(ex.giveQuantity),
        productToReceiveId: ex.receiveProduct,
        quantityToReceive: Number(ex.receiveQuantity),
        operation: "EXCHANGE",
        fullName: ex.fullName,
        gstNumber: ex.gst || undefined,
      };
    }

    return { localBuyDraft, localSellPayload, localExchangeDraft };
  };

  const handleCompleteRequest = async () => {
    if (!authStorage.hasSession()) {
      setSubmitError("You are not logged in. Please login and try again.");
      return;
    }

    const draft = buildDraft();
    if (!draft) return;
    const { localBuyDraft, localSellPayload, localExchangeDraft } = draft;

    setLoading(true);
    try {
      if (variant === "buy") {
        if (!localBuyDraft) throw new Error("Missing purchase details.");
        const product = await userService.getProductById(
          localBuyDraft.productId,
        );
        const buyPrice =
          parseFloat(
            (product as { buyPrice?: string; price?: string } | null)
              ?.buyPrice ??
            (product as { price?: string } | null)?.price ??
            "0",
          ) || 0;
        await userService.createBuy({
          ...localBuyDraft,
          amount: buyPrice * localBuyDraft.quantity * 1.05,
        });
      } else if (variant === "sell") {
        if (!localSellPayload) throw new Error("Missing sell details.");
        await sellService.sellProduct(localSellPayload);
      } else {
        if (!localExchangeDraft) throw new Error("Missing exchange details.");
        await userService.createExchange({
          ...localExchangeDraft,
          amount: extraPayableAmount,
          shippingAddress: userDetails?.address || "",
          city: userDetails?.city || "",
          pincode: getUserPincode(userDetails),
        });
      }
      onClose();
    }
    catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        const details = e.response?.data?.details;

        if (Array.isArray(details)) {
          const errors: Record<string, string> = {};

          details.forEach((item: { field: string; message: string }) => {
            errors[item.field] =
              item.message.charAt(0).toUpperCase() + item.message.slice(1);
          });

          setFieldErrors(errors);
          return;
        }
      }

      setSubmitError(
        getApiErrorMessage(e, "Failed to submit request. Please try again.")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoToSample = () => {
    const draft = buildDraft();
    if (!draft) return;
    const { localBuyDraft, localSellPayload, localExchangeDraft } = draft;
    const payload =
      localBuyDraft ?? localSellPayload ?? localExchangeDraft ?? undefined;
    onSuccess(variant, payload);
    onClose();
  };

  if (!isOpen) return null;

  const PANEL_CONTENT: Record<
    TradeVariant,
    { title: string; description: string }
  > = {
    buy: {
      title: t("panel.buy.title"),
      description: t("panel.buy.description"),
    },
    sell: {
      title: t("panel.sell.title"),
      description: t("panel.sell.description"),
    },
    exchange: {
      title: t("panel.exchange.title"),
      description: t("panel.exchange.description"),
    },
  };

  const { size, sidebarWidth, padding, footerPadding } = MODAL_CONFIG[variant];
  const { title, description } = PANEL_CONTENT[variant];
  const isPremiumUser = Boolean(userDetails?.isPremium);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm px-4 pt-24 pb-6">
      <div
        className={`relative w-full ${size} max-h-[78vh] rounded-2xl overflow-hidden bg-white shadow-xl border border-slate-100 flex flex-col`}
      >
        {loading && <ModalLoadingOverlay message={t("submit.submitting")} />}
        <button
          type="button"
          onClick={onClose}
          className={`absolute right-2 top-2 z-10 transition-colors cursor-pointer ${CLS.closeBtn}`}
        >
          {Icon.close}
        </button>
        <div className="flex flex-col sm:flex-row flex-1 min-h-0">
          <div
            className={`
                bg-linear-to-br from-[#0A2540] to-[#1e3a5f]
                sm:${sidebarWidth} sm:min-w-0 sm:shrink-0
                flex flex-row items-center gap-3 px-5 py-4
                sm:flex-col sm:justify-center sm:px-5 sm:py-6 sm:gap-0
              `}
          >
            <div className="shrink-0 [&_svg]:h-10 [&_svg]:w-10 [&_svg]:text-white/90 sm:[&_svg]:h-32 sm:[&_svg]:w-32 sm:flex sm:justify-center sm:w-full sm:mb-3">
              {variant === "exchange" ? Icon.arrowUp : Icon.box}
            </div>
            <div>
              <h2 className={`${CLS.sidebarTitle} mb-0.5 sm:mb-1.5`}>
                {title}
              </h2>
              <p className={CLS.sidebarDesc}>{description}</p>
            </div>
          </div>
          <div className="flex-1 min-w-0 flex flex-col min-h-0">
            <div className={`${padding} overflow-y-auto flex-1 min-h-0`}>
              <>
                {variant === "buy" && (
                  <TradeModalBuyForm
                    buy={buy}
                    buyProducts={buyProducts}
                    setBuy={setBuy}
                    fieldErrors={fieldErrors}
                    setFieldErrors={setFieldErrors}
                    buyMinQuantity={buyMinQuantity}
                    buyProductPrice={buyProductPrice}
                    selectedProduct={selectedProduct}
                    setBuyField={setBuyField}
                    inp={inp}
                    parseNum={parseNum}
                    CLS={CLS}
                  />
                )}
                {variant === "sell" && (
                  <TradeModalSellForm
                    Icon={Icon}
                    fieldErrors={fieldErrors}
                    selectedProduct={selectedProduct}
                    sell={sell}
                    setSell={setSell}
                    sellMinQuantity={sellMinQuantity}
                    sellProducts={sellProducts}
                    inp={inp}
                    CLS={CLS}
                    setFieldErrors={setFieldErrors}
                    setSellField={setSellField}
                  />
                )}
                {variant === "exchange" && (
                  <TradeModalExchangeForm
                    ex={ex}
                    setEx={setEx}
                    fieldErrors={fieldErrors}
                    setFieldErrors={setFieldErrors}
                    selectedProduct={selectedProduct}
                    giveMinQuantity={giveMinQuantity}
                    exchangeProducts={exchangeProducts}
                    setExField={setExField}
                    inp={inp}
                    CLS={CLS}
                    giveProductPrice={giveProductPrice}
                    allProducts={allProducts}
                    receiveProductPrice={receiveProductPrice}
                    extraPayableAmount={extraPayableAmount}
                    Icon={Icon}
                  />
                )}
                {error && <p className={CLS.error}>{error}</p>}
                {submitError && (
                  <p className="mt-2 text-sm text-red-600">{submitError}</p>
                )}
              </>
            </div>
            <TradeModalFooter
              footerPadding={footerPadding}
              handleCompleteRequest={handleCompleteRequest}
              loading={loading}
              CLS={CLS}
              variant={variant}
              tm={tm}
              Icon={Icon}
              WhatsAppButton={WhatsAppButton}
              handleGoToSample={handleGoToSample}
              isPremiumUser={isPremiumUser}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
